import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  RepairBumperPaymentTypeEnum,
  RepairDto,
  RepairFormulaCreateDto,
  RepairFormulaDto,
  RepairPaymentTypeEnum,
  RepairResourceTypeEnum,
} from '@vatos-pas/common';

export interface RepairOption {
  id: string;
  name: string;
}

export interface RepairFormulaFormValues {
  repairId: string;
  repairResourceType: RepairResourceTypeEnum | '';
  repairBumperPaymentType: RepairBumperPaymentTypeEnum | '';
  repairPaymentType: RepairPaymentTypeEnum | '';
  active: boolean;
}

/**
 * Adapter between the data layer and repairs-settings components — components
 * stay data-layer agnostic so the underlying client (react-admin, react-query,
 * Apollo, fetch) can be swapped without touching them.
 *
 * `useGetOne` provides SWR semantics via the react-admin Redux cache: cached
 * data returns immediately, refetch happens in the background, and the loading
 * spinner only shows on first load when no cached data exists.
 */
function useRepairsSettings() {
  const dataProvider = useDataProvider();

  const [formulaId, setFormulaId] = useState<string>('');
  const {
    data: selectedFormula,
    loading: formulaLoading,
    loaded: formulaLoaded,
    error: formulaError,
  } = useGetOne<RepairFormulaDto>('repair-formula', formulaId, {
    enabled: !!formulaId,
  });

  const isLoading = !!formulaId && !selectedFormula && formulaLoading;

  const [formLoading, setFormLoading] = useState(false);

  const loadFormulaById = useCallback((id: string): void => {
    setFormulaId(id);
  }, []);

  const createFormula = useCallback(
    async (data: RepairFormulaCreateDto): Promise<RepairFormulaDto> => {
      const result = await dataProvider.create<RepairFormulaDto>(
        'repair-formula',
        { data },
      );
      setFormulaId(result.data.id);
      return result.data;
    },
    [dataProvider],
  );

  const updateFormula = useCallback(
    async (
      id: string,
      data: Partial<RepairFormulaDto>,
    ): Promise<RepairFormulaDto> => {
      const result = await dataProvider.update<RepairFormulaDto>(
        'repair-formula',
        {
          id,
          data: { ...selectedFormula, ...data },
          previousData: selectedFormula || { id },
        },
      );
      return result.data;
    },
    [dataProvider, selectedFormula],
  );

  const loadRepairOptions = useCallback(
    async (_searchTerm: string): Promise<RepairOption[]> => {
      const result = await dataProvider.getList<RepairDto>('repair', {
        filter: {},
        pagination: { page: 1, perPage: Number.MAX_SAFE_INTEGER },
        sort: { field: 'description', order: 'ASC' },
      });
      return result.data.map(repair => ({
        id: repair.id,
        name: repair.description,
      }));
    },
    [dataProvider],
  );

  return {
    selectedFormula,
    isLoading,
    formulaLoaded,
    formulaError,
    formLoading,
    setFormLoading,
    loadFormulaById,
    createFormula,
    updateFormula,
    loadRepairOptions,
  };
}

export const RepairsSettingsContainer = createContainer(useRepairsSettings);
